// frontend/src/pages/Finance/InvoiceInbox.jsx
// ✅ VERSIÓN MEJORADA - MUESTRA TODOS LOS DATOS DEL ODS EN FACTURAS PENDIENTES

import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Collapse,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label
} from 'reactstrap';
import { invoiceSchedulesAPI, invoicesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InvoiceUploadModal from '../../components/Modals/InvoiceUploadModal';
import PDFViewerModal from '../../components/Modals/PDFViewerModal';
import { generateInvoiceBillingPDF } from '../../utils/pdfGenerator';
import { buildCostBreakdown, conceptHasAmount, hasAnyCost } from '../../utils/costBreakdown';
import { parseDate } from '../../utils/dates';

const InvoiceInbox = () => {
  const { user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [schedules, setSchedules] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Modales
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [cancellingInvoice, setCancellingInvoice] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilterType, setDateFilterType] = useState('all');
  const [dayFilter, setDayFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    overdue: 0,
    upcoming: 0,
    completed: 0
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, dateFilterType, dayFilter, monthFilter, customStartDate, customEndDate, rowsPerPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'pending') {
        const response = await invoiceSchedulesAPI.getPending();
        const data = response.data.schedules || response.data || [];
        setSchedules(data);
        setInvoices([]);
        
        // Calcular stats
        setStats({
          total: data.length,
          overdue: data.filter(s => s.is_overdue).length,
          upcoming: data.filter(s => !s.is_overdue && s.days_until_invoice <= 3).length,
          completed: 0
        });
      } else {
        const statusByTab = {
          invoiced: 'pending',
          completed: 'paid',
          cancelled: 'cancelled'
        };
        const response = await invoicesAPI.getAll({ 
          status: statusByTab[activeTab] || 'pending',
          page: 1,
          page_size: 1000,
          ordering: '-issue_date'
        });
        const data = response.data.results || response.data || [];
        setInvoices(data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('No se pudieron cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openUploadModal = (schedule) => {
    setSelectedSchedule(schedule);
    setUploadModalOpen(true);
  };

  const handleDownloadInfo = (schedule) => {
    generateInvoiceBillingPDF(schedule);
  };

  const handleUploadConfirm = async (data) => {
    try {
      await invoicesAPI.confirm(data);
      setSuccess('Factura procesada exitosamente');
      setUploadModalOpen(false);
      loadData();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error('Error confirmando factura:', err);
      throw err;
    }
  };

  const viewPDF = (url, title) => {
    // ✅ VALIDAR QUE URL EXISTA
    if (!url) {
      setError('El PDF no está disponible para esta factura');
      return;
   }
  
    const fullUrl = url.startsWith('http') ? url : `http://localhost:8000${url}`;
    setPdfUrl(fullUrl);
    setPdfTitle(title);
    setPdfViewerOpen(true);
  };

  const openCancelModal = (invoice) => {
    setInvoiceToCancel(invoice);
    setCancellationReason('');
    setCancelError('');
    setCancelModalOpen(true);
  };

  const closeCancelModal = (force = false) => {
    if (cancellingInvoice && !force) return;
    setCancelModalOpen(false);
    setInvoiceToCancel(null);
    setCancellationReason('');
    setCancelError('');
  };

  const handleCancelInvoice = async () => {
    const reason = cancellationReason.trim();
    if (!reason) {
      setCancelError('Escribe el motivo de la cancelación');
      return;
    }

    try {
      setCancellingInvoice(true);
      setCancelError('');
      const response = await invoicesAPI.cancel(invoiceToCancel.id, { reason });
      setSuccess(response.data.message || `Factura ${invoiceToCancel.folio} cancelada`);
      closeCancelModal(true);
      setActiveTab('pending');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const apiError = err.response?.data?.error;
      const errorMessage = typeof apiError === 'string'
        ? apiError
        : apiError?.reason?.[0] || 'No se pudo cancelar la factura';
      setCancelError(errorMessage);
    } finally {
      setCancellingInvoice(false);
    }
  };

  const openDeleteModal = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteError('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = (force = false) => {
    if (deletingInvoice && !force) return;
    setDeleteModalOpen(false);
    setInvoiceToDelete(null);
    setDeleteError('');
  };

  const handleDeleteInvoice = async () => {
    try {
      setDeletingInvoice(true);
      setDeleteError('');
      const response = await invoicesAPI.delete(invoiceToDelete.id);
      setSuccess(response.data.message || `Factura ${invoiceToDelete.folio} eliminada`);
      closeDeleteModal(true);
      setInvoices((prev) => prev.filter((inv) => inv.id !== invoiceToDelete.id));
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      const apiError = err.response?.data?.error;
      const errorMessage = typeof apiError === 'string'
        ? apiError
        : 'No se pudo eliminar la factura';
      setDeleteError(errorMessage);
    } finally {
      setDeletingInvoice(false);
    }
  };

  const getStatusBadge = (schedule) => {
    if (schedule.status === 'invoice_uploaded') {
      return { color: 'success', text: 'FACTURADA', icon: 'ni-check-bold' };
    } else if (schedule.is_overdue) {
      return { color: 'danger', text: 'VENCIDA', icon: 'ni-fat-remove' };
    } else if (schedule.days_until_invoice <= 3) {
      return { color: 'warning', text: 'PRÓXIMA', icon: 'ni-time-alarm' };
    } else {
      return { color: 'info', text: 'PENDIENTE', icon: 'ni-calendar-grid-58' };
    }
  };

  const getDaysText = (days) => {
    if (days < 0) {
      return <span className="text-danger font-weight-bold">{Math.abs(days)} días vencido</span>;
    } else if (days === 0) {
      return <span className="text font-weight-bold">HOY</span>;
    } else if (days === 1) {
      return <span className="text-warning">Mañana</span>;
    } else if (days <= 3) {
      return <span className="text-warning">{days} días</span>;
    } else {
      return <span className="text-muted">{days} días</span>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const normalizeDateString = (dateValue) => {
    if (!dateValue) return '';
    return String(dateValue).slice(0, 10);
  };

  const filteredInvoices = useMemo(() => {
    if (activeTab === 'pending') return [];

    const searchValue = searchTerm.trim().toLowerCase();
    return invoices.filter((invoice) => {
      if (searchValue) {
        const haystack = [
          invoice.folio,
          invoice.client_name,
          invoice.cliente_nombre,
          invoice.uuid,
          invoice.oc_numero,
          invoice.order_number,
          invoice.cliente_rfc,
          invoice.client_rfc
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(searchValue)) return false;
      }

      const issueDate = normalizeDateString(invoice.issue_date);
      if (!issueDate) return dateFilterType === 'all';

      if (dateFilterType === 'day' && dayFilter) {
        return issueDate === dayFilter;
      }

      if (dateFilterType === 'month' && monthFilter) {
        return issueDate.slice(0, 7) === monthFilter;
      }

      if (dateFilterType === 'custom') {
        if (customStartDate && issueDate < customStartDate) return false;
        if (customEndDate && issueDate > customEndDate) return false;
      }

      return true;
    });
  }, [activeTab, invoices, searchTerm, dateFilterType, dayFilter, monthFilter, customStartDate, customEndDate]);

  const totalInvoicePages = useMemo(() => {
    if (activeTab === 'pending') return 1;
    return Math.max(1, Math.ceil(filteredInvoices.length / rowsPerPage));
  }, [activeTab, filteredInvoices.length, rowsPerPage]);

  useEffect(() => {
    if (activeTab !== 'pending' && currentPage > totalInvoicePages) {
      setCurrentPage(totalInvoicePages);
    }
  }, [activeTab, currentPage, totalInvoicePages]);

  const visibleInvoices = useMemo(() => {
    if (activeTab === 'pending') return invoices;
    const start = (currentPage - 1) * rowsPerPage;
    return filteredInvoices.slice(start, start + rowsPerPage);
  }, [activeTab, invoices, filteredInvoices, currentPage, rowsPerPage]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return parseDate(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // ✅ FUNCIONES AUXILIARES PARA EXTRAER DATOS
  const getServiceDetails = (schedule) => {
    if (!schedule) return {};
    const quoteItems = schedule.quote_details?.items || {};
    return quoteItems.service_details || {};
  };

  const getClientInfo = (schedule) => {
    if (!schedule) return {};
    const quoteItems = schedule.quote_details?.items || {};
    return quoteItems.client_info || {};
  };

  const getCosts = (schedule) => {
    if (!schedule) return {};
    const quoteItems = schedule.quote_details?.items || {};
    return buildCostBreakdown(quoteItems);
  };

  if (loading && schedules.length === 0 && invoices.length === 0) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando panel de facturación...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      {/* ============================================ */}
      {/* HEADER GRADIENT */}
      {/* ============================================ */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
      
      <Container fluid className="py-4">
        {/* ============================================ */}
        {/* TÍTULO Y ACCIONES */}
        {/* ============================================ */}
        <Row className="mb-4">
          <Col md="6">
            <h6 className="text-white mb-0">
              <i className="ni ni-archive-2 me-2"></i>
              Panel de Facturación
            </h6>
            <p className="text-white text-sm opacity-8 mb-0">
              Gestiona facturas pendientes y sube nuevas facturas
            </p>
          </Col>
          <Col md="6" className="text-end">
            <Button 
              color="white" 
              size="sm"
              className="shadow-sm"
              onClick={() => openUploadModal(null)}
            >
              <i className="ni ni-cloud-upload-96 me-2"></i>
              Subir Nueva Factura
            </Button>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* ALERTAS */}
        {/* ============================================ */}
        {success && (
          <Alert color="success" className="shadow-sm" toggle={() => setSuccess(null)}>
            <span className="alert-icon"><i className="ni ni-like-2"></i></span>
            <span className="alert-text">{success}</span>
          </Alert>
        )}

        {error && (
          <Alert color="danger" className="shadow-sm" toggle={() => setError(null)}>
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
          </Alert>
        )}

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        <Row>
          {/* Card 1: Total Pendientes */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Total Pendientes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">
                        {stats.total}
                      </h5>
                      <p className="mb-0">
                        <span className="text-xs text-muted">facturas por emitir</span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-calendar-grid-58 text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 2: Vencidas */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Vencidas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-danger">
                        {stats.overdue}
                      </h5>
                      <p className="mb-0">
                        <span className="text text-xs font-weight-bold">
                          <i className="ni ni-fat-remove me-1"></i>
                          requieren atención
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-danger shadow-danger text-center rounded-circle">
                      <i className="ni ni-fat-remove text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 3: Próximas (3 días) */}
          <Col xl="3" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Próximas (3 días)
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-warning">
                        {stats.upcoming}
                      </h5>
                      <p className="mb-0">
                        <span className="text text-xs font-weight-bold">
                          <i className="ni ni-time-alarm me-1"></i>
                          por vencer pronto
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                      <i className="ni ni-time-alarm text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Card 4: Completadas Este Mes */}
          <Col xl="3" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Completadas Este Mes
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-success">
                        {stats.completed}
                      </h5>
                      <p className="mb-0">
                        <span className="text text-xs font-weight-bold">
                          <i className="ni ni-check-bold me-1"></i>
                          facturas emitidas
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-check-bold text-lg opacity-10 text-white" aria-hidden="true"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TABLA PRINCIPAL CON TABS */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col>
            <Card className="shadow border-0">
              <CardHeader className="bg-transparent pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <Nav tabs className="nav-tabs-custom">
                      <NavItem>
                        <NavLink
                          className={`${activeTab === 'pending' ? 'active' : ''}`}
                          onClick={() => setActiveTab('pending')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ni ni-time-alarm me-2"></i>
                          Pendientes de Facturar
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`${activeTab === 'invoiced' ? 'active' : ''}`}
                          onClick={() => setActiveTab('invoiced')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ni ni-single-copy-04 me-2"></i>
                          Facturas Emitidas
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`${activeTab === 'completed' ? 'active' : ''}`}
                          onClick={() => setActiveTab('completed')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ni ni-check-bold me-2"></i>
                          Facturas Pagadas
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`${activeTab === 'cancelled' ? 'active' : ''}`}
                          onClick={() => setActiveTab('cancelled')}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="ni ni-fat-remove me-2"></i>
                          Canceladas
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </Col>
                  <Col md="6" className="text-end">
                    <p className="text-xs text-muted mb-0 mt-2">
                      {activeTab === 'pending' && `${schedules.length} registros`}
                      {activeTab !== 'pending' && `${filteredInvoices.length} registros`}
                    </p>
                  </Col>
                </Row>
                {activeTab !== 'pending' && (
                  <Row className="mt-3 g-2">
                    <Col md="4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Buscar por folio, cliente, UUID, OC o RFC"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                    <Col md="2">
                      <select
                        className="form-select form-select-sm"
                        value={dateFilterType}
                        onChange={(e) => setDateFilterType(e.target.value)}
                      >
                        <option value="all">Sin filtro de fecha</option>
                        <option value="day">Por dia</option>
                        <option value="month">Por mes</option>
                        <option value="custom">Rango personalizado</option>
                      </select>
                    </Col>
                    <Col md="2">
                      {dateFilterType === 'day' && (
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={dayFilter}
                          onChange={(e) => setDayFilter(e.target.value)}
                        />
                      )}
                      {dateFilterType === 'month' && (
                        <input
                          type="month"
                          className="form-control form-control-sm"
                          value={monthFilter}
                          onChange={(e) => setMonthFilter(e.target.value)}
                        />
                      )}
                    </Col>
                    <Col md="2">
                      {dateFilterType === 'custom' && (
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                        />
                      )}
                    </Col>
                    <Col md="2">
                      {dateFilterType === 'custom' && (
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                        />
                      )}
                      {dateFilterType !== 'custom' && (
                        <select
                          className="form-select form-select-sm"
                          value={rowsPerPage}
                          onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                          <option value={10}>10 por pagina</option>
                          <option value={25}>25 por pagina</option>
                          <option value={50}>50 por pagina</option>
                          <option value={100}>100 por pagina</option>
                        </select>
                      )}
                    </Col>
                  </Row>
                )}
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <TabContent activeTab={activeTab}>
                  {/* ============================================ */}
                  {/* TAB: PENDIENTES */}
                  {/* ============================================ */}
                  <TabPane tabId="pending">
                    <div className="table-responsive p-0">
                      <Table className="align-items-center mb-0" hover>
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Orden / Cliente
                            </th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                              Monto
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Fecha de Facturacion
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Estado
                            </th>
                            <th className="text-secondary opacity-7"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedules.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <i className="ni ni-check-bold text-success" style={{ fontSize: '3rem' }}></i>
                                <p className="text-xs text-secondary mb-0 mt-3">
                                  No hay facturas pendientes
                                </p>
                                <small className="text-muted">¡Excelente! Estás al día con la facturación</small>
                              </td>
                            </tr>
                          ) : (
                            schedules.map((schedule) => {
                              const statusBadge = getStatusBadge(schedule);
                              const isExpanded = expandedRows[schedule.id];
                              const serviceDetails = getServiceDetails(schedule);
                              const clientInfo = getClientInfo(schedule);
                              const costs = getCosts(schedule);
                              
                              return (
                                <React.Fragment key={schedule.id}>
                                  <tr style={{ cursor: 'pointer' }} onClick={() => toggleRowExpansion(schedule.id)}>
                                    {/* Orden / Cliente */}
                                    <td>
                                      <div className="d-flex px-2 py-1">
                                        <div className="icon icon-shape icon-sm bg-gradient-info shadow text-center rounded-circle me-2">
                                          <i className="ni ni-box-2 text-white text-xs"></i>
                                        </div>
                                        <div className="d-flex flex-column justify-content-center">
                                          <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                            {schedule.order_number}
                                          </h6>
                                          <p className="text-xs text-secondary mb-0">
                                            {schedule.client_name}
                                          </p>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Monto */}
                                    <td>
                                      <p className="text-sm font-weight-bold mb-0 text-dark">
                                        {formatCurrency(schedule.scheduled_amount)}
                                      </p>
                                      <small className="text-xs text-muted">
                                        + IVA = {formatCurrency(parseFloat(schedule.scheduled_amount || 0) * 1.16)}
                                      </small>
                                      {parseFloat(schedule.scheduled_amount || 0) < parseFloat(schedule.order_subtotal || 0) && (
                                        <small className="text-xs text-warning d-block">
                                          Parcialidad de orden: {formatCurrency(schedule.order_subtotal)}
                                        </small>
                                      )}
                                    </td>

                                    {/* Fecha de Facturacion */}
                                    <td className="align-middle text-center">
                                      <span className="text-secondary text-xs font-weight-bold">
                                        {parseDate(schedule.scheduled_invoice_date).toLocaleDateString('es-MX', {
                                          day: '2-digit',
                                          month: 'short',
                                          year: 'numeric'
                                        })}
                                      </span>
                                      <br />
                                      <small>{getDaysText(schedule.days_until_invoice)}</small>
                                    </td>

                                    {/* Estado */}
                                    <td className="align-middle text-center">
                                      <Badge color="primary" className={`badge-sm bg-gradient-${statusBadge.color} shadow-sm`}>
                                        <i className={`ni ${statusBadge.icon} me-1`}></i>
                                        {statusBadge.text}
                                      </Badge>
                                    </td>

                                    {/* Acciones */}
                                    <td className="align-middle">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="text-secondary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRowExpansion(schedule.id);
                                        }}
                                      >
                                        <i className={`ni ni-bold-${isExpanded ? 'up' : 'down'}`}></i>
                                      </Button>
                                      <Button
                                        color="secondary"
                                        size="sm"
                                        className="btn-icon shadow-sm me-1"
                                        title="Descargar info de facturación"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadInfo(schedule);
                                        }}
                                      >
                                        <span className="btn-inner--icon">
                                          
                                          Descargar orden de servicio
                                        </span>
                                      </Button>
                                      <Button
                                        color="primary"
                                        size="sm"
                                        className="btn-icon shadow-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openUploadModal(schedule);
                                        }}
                                      >
                                        <span className="btn-inner--icon">
                                          
                                          Subir Factura
                                        </span>
                                      </Button>
                                    </td>
                                  </tr>
                                  
                                  {/* ============================================ */}
                                  {/* FILA EXPANDIBLE CON TODOS LOS DATOS DEL ODS */}
                                  {/* ============================================ */}
                                  <tr>
                                    <td colSpan="5" className="p-0 border-0">
                                      <Collapse isOpen={isExpanded}>
                                        <div className="p-4 bg-gray-100">
                                          <Row>
                                            {/* ========== COLUMNA 1: DATOS DEL CLIENTE ========== */}
                                            <Col md="4" className="mb-3">
                                              <Card className="shadow-sm border-0 h-100">
                                                <CardBody className="p-3">
                                                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                    <i className="ni ni-building me-2"></i>
                                                    Datos del Cliente
                                                  </h6>
                                                  <Table borderless size="sm" className="mb-0">
                                                    <tbody>
                                                      <tr>
                                                        <td className="text-muted text-xs" style={{ width: '45%' }}>Nombre:</td>
                                                        <td className="text-dark text-xs font-weight-bold">{schedule.billing_name}</td>
                                                      </tr>
                                                      <tr>
                                                        <td className="text-muted text-xs">RFC:</td>
                                                        <td className="text-dark text-xs font-weight-bold">
                                                          <code className="text-primary">{schedule.billing_rfc}</code>
                                                        </td>
                                                      </tr>
                                                      <tr>
                                                        <td className="text-muted text-xs">Dirección:</td>
                                                        <td className="text-dark text-xs">{schedule.billing_address}</td>
                                                      </tr>
                                                    </tbody>
                                                  </Table>
                                                </CardBody>
                                              </Card>
                                            </Col>

                                            {/* ========== COLUMNA 2: MONTOS ========== */}
                                            <Col md="4" className="mb-3">
                                              <Card className="shadow-sm border-0 h-100">
                                                <CardBody className="p-3">
                                                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                    <i className="ni ni-money-coins me-2"></i>
                                                    Montos a Facturar
                                                  </h6>
                                                  <Table borderless size="sm" className="mb-0">
                                                    <tbody>
                                                      <tr>
                                                        <td className="text-muted text-xs" style={{ width: '45%' }}>Subtotal:</td>
                                                        <td className="text-dark text-xs font-weight-bold text-end">
                                                          {formatCurrency(parseFloat(schedule.scheduled_amount || 0))}
                                                        </td>
                                                      </tr>
                                                      <tr>
                                                        <td className="text-muted text-xs">IVA (16%):</td>
                                                        <td className="text-dark text-xs font-weight-bold text-end">
                                                          {formatCurrency(parseFloat(schedule.scheduled_amount || 0) * 0.16)}
                                                        </td>
                                                      </tr>
                                                      <tr className="border-top">
                                                        <td className="text-muted text-xs font-weight-bold">Total:</td>
                                                        <td className="text-primary text-xs font-weight-bold text-end">
                                                          {formatCurrency(parseFloat(schedule.scheduled_amount || 0) * 1.16)}
                                                        </td>
                                                      </tr>
                                                      <tr className="border-top">
                                                        <td className="text-muted text-xs">Días Crédito:</td>
                                                        <td className="text-end">
                                                          <Badge color="info" className="badge-sm">
                                                            {schedule.credit_days || 0} días
                                                          </Badge>
                                                        </td>
                                                      </tr>
                                                    </tbody>
                                                  </Table>
                                                </CardBody>
                                              </Card>
                                            </Col>

                                            {/* ========== COLUMNA 3: LOGÍSTICA ========== */}
                                            <Col md="4" className="mb-3">
                                              <Card className="shadow-sm border-0 h-100">
                                                <CardBody className="p-3">
                                                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                    <i className="ni ni-delivery-fast me-2"></i>
                                                    Logística
                                                  </h6>
                                                  <Table borderless size="sm" className="mb-0">
                                                    <tbody>
                                                      {schedule.facilitator_name && (
                                                        <tr>
                                                          <td className="text-muted text-xs" style={{ width: '45%' }}>Facilitador:</td>
                                                          <td className="text-dark text-xs font-weight-bold">{schedule.facilitator_name}</td>
                                                        </tr>
                                                      )}
                                                      {schedule.location && (
                                                        <tr>
                                                          <td className="text-muted text-xs">Lugar:</td>
                                                          <td className="text-dark text-xs">{schedule.location}</td>
                                                        </tr>
                                                      )}
                                                      {schedule.service_delivery_date && (
                                                        <tr>
                                                          <td className="text-muted text-xs">Fecha Inicio:</td>
                                                          <td className="text-dark text-xs font-weight-bold">
                                                            {formatDate(schedule.service_delivery_date)}
                                                          </td>
                                                        </tr>
                                                      )}
                                                      {schedule.total_hours && (
                                                        <tr>
                                                          <td className="text-muted text-xs">Horas:</td>
                                                          <td className="text-dark text-xs">
                                                            <Badge color="primary" className="badge-sm">
                                                              {schedule.total_hours} hrs
                                                            </Badge>
                                                          </td>
                                                        </tr>
                                                      )}
                                                    </tbody>
                                                  </Table>
                                                </CardBody>
                                              </Card>
                                            </Col>

                                            {/* ========== DETALLES DEL SERVICIO ========== */}
                                            <Col md="12" className="mb-3">
                                              <Card className="shadow-sm border-0">
                                                <CardBody className="p-3">
                                                  <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                    <i className="ni ni-box-2 me-2"></i>
                                                    Descripción del Servicio
                                                  </h6>
                                                  <Row>
                                                    <Col md="8">
                                                      <Table borderless size="sm" className="mb-0">
                                                        <tbody>
                                                          {schedule.description && (
                                                            <tr>
                                                              <td className="text-muted text-xs" style={{ width: '30%' }}>Descripción:</td>
                                                              <td className="text-dark text-xs" style={{ whiteSpace: 'pre-line' }}>
                                                                {schedule.description}
                                                              </td>
                                                            </tr>
                                                          )}
                                                          {(serviceDetails.service_type || schedule.quote_details?.service_type) && (
                                                            <tr>
                                                              <td className="text-muted text-xs">Nombre Servicio:</td>
                                                              <td className="text-dark text-xs font-weight-bold">
                                                                {serviceDetails.service_type || schedule.quote_details?.service_type}
                                                              </td>
                                                            </tr>
                                                          )}
                                                          {(serviceDetails.category || schedule.quote_details?.category) && (
                                                            <tr>
                                                              <td className="text-muted text-xs">Categoría:</td>
                                                              <td className="text-dark text-xs">
                                                                <Badge color="primary" className="badge-sm">
                                                                                                                                    {serviceDetails.category || schedule.quote_details?.category}
                                                                </Badge>
                                                              </td>
                                                            </tr>
                                                          )}
                                                          {serviceDetails.num_people && (
                                                            <tr>
                                                              <td className="text-dark text-xs">Participantes:</td>
                                                              <td className="text-dark text-xs">
                                                                <Badge color="primary" className="badge-sm">
                                                                  {serviceDetails.num_people}
                                                                </Badge>
                                                              </td>
                                                            </tr>
                                                          )}
                                                          {serviceDetails.num_groups && (
                                                            <tr>
                                                              <td className="text-muted text-xs">Grupos:</td>
                                                              <td className="text-dark text-xs">
                                                                <Badge color="primary" className="badge-sm">
                                                                  {serviceDetails.num_groups}
                                                                </Badge>
                                                              </td>
                                                            </tr>
                                                          )}
                                                          {schedule.hourly_rate && (
                                                            <tr>
                                                              <td className="text-muted text-xs">Precio/Hora:</td>
                                                              <td className="text-dark text-xs font-weight-bold">
                                                                {formatCurrency(schedule.hourly_rate)}
                                                              </td>
                                                            </tr>
                                                          )}
                                                        </tbody>
                                                      </Table>
                                                    </Col>
                                                    <Col md="4">
                                                      {/* Contacto */}
                                                      {(clientInfo.contact_name || schedule.quote_details?.contact_name) && (
                                                        <div className="bg-light p-2 border-radius-lg">
                                                          <p className="text-xs text-uppercase text-muted mb-1 font-weight-bold">
                                                            <i className="ni ni-single-02 me-1"></i>
                                                            Contacto del Servicio
                                                          </p>
                                                          <p className="text-xs mb-1">
                                                            <strong>Nombre:</strong> {clientInfo.contact_name || schedule.quote_details?.contact_name}
                                                          </p>
                                                          {(clientInfo.contact_email || schedule.quote_details?.contact_email) && (
                                                            <p className="text-xs mb-0">
                                                              <strong>Correo:</strong>{' '}
                                                              <a 
                                                                href={`mailto:${clientInfo.contact_email || schedule.quote_details?.contact_email}`}
                                                                className="text-primary"
                                                              >
                                                                {clientInfo.contact_email || schedule.quote_details?.contact_email}
                                                              </a>
                                                            </p>
                                                          )}
                                                        </div>
                                                      )}
                                                    </Col>
                                                  </Row>
                                                </CardBody>
                                              </Card>
                                            </Col>

                                            {/* ========== DESCRIPCIÓN DE LA FACTURA (COSTOS) ========== */}
                                            {(() => {
                                              const hasCosts = hasAnyCost(costs);
                                              if (!hasCosts) return null;

                                              return (
                                                <Col md="12" className="mb-3">
                                                  <Card className="shadow-sm border-0">
                                                    <CardBody className="p-3">
                                                      <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                        <i className="ni ni-chart-bar-32 me-2"></i>
                                                        Descripción de la Factura (Desglose de Costos)
                                                      </h6>
                                                      <Table size="sm" className="mb-0">
                                                        <thead className="thead-light">
                                                          <tr>
                                                            <th className="text-xs">Concepto</th>
                                                            <th className="text-xs text-end">Cobrado (sin IVA)</th>
                                                            <th className="text-xs text-end">Cobrado (con IVA)</th>
                                                            <th className="text-xs text-end">Gastado</th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {conceptHasAmount(costs.viaticos) && (
                                                            <tr>
                                                              <td className="text-xs font-weight-bold">VIÁTICOS</td>
                                                              <td className="text-xs text-end">{formatCurrency((costs.viaticos.cobrado || 0) / 1.16)}</td>
                                                              <td className="text-xs text-end text-muted">
                                                                <small>+ IVA = {formatCurrency(costs.viaticos.cobrado || 0)}</small>
                                                              </td>
                                                              <td className="text-xs text-end">{formatCurrency(costs.viaticos.gastado || 0)}</td>
                                                            </tr>
                                                          )}
                                                          {conceptHasAmount(costs.lugar) && (
                                                            <tr>
                                                              <td className="text-xs font-weight-bold">LUGAR</td>
                                                              <td className="text-xs text-end">{formatCurrency((costs.lugar.cobrado || 0) / 1.16)}</td>
                                                              <td className="text-xs text-end text-muted">
                                                                <small>+ IVA = {formatCurrency(costs.lugar.cobrado || 0)}</small>
                                                              </td>
                                                              <td className="text-xs text-end">{formatCurrency(costs.lugar.gastado || 0)}</td>
                                                            </tr>
                                                          )}
                                                          {conceptHasAmount(costs.material) && (
                                                            <tr>
                                                              <td className="text-xs font-weight-bold">MATERIAL</td>
                                                              <td className="text-xs text-end">{formatCurrency((costs.material.cobrado || 0) / 1.16)}</td>
                                                              <td className="text-xs text-end text-muted">
                                                                <small>+ IVA = {formatCurrency(costs.material.cobrado || 0)}</small>
                                                              </td>
                                                              <td className="text-xs text-end">{formatCurrency(costs.material.gastado || 0)}</td>
                                                            </tr>
                                                          )}
                                                          {conceptHasAmount(costs.otro) && (
                                                            <tr>
                                                              <td className="text-xs font-weight-bold">OTRO</td>
                                                              <td className="text-xs text-end">{formatCurrency((costs.otro.cobrado || 0) / 1.16)}</td>
                                                              <td className="text-xs text-end text-muted">
                                                                <small>+ IVA = {formatCurrency(costs.otro.cobrado || 0)}</small>
                                                              </td>
                                                              <td className="text-xs text-end">{formatCurrency(costs.otro.gastado || 0)}</td>
                                                            </tr>
                                                          )}
                                                        </tbody>
                                                      </Table>

                                                    </CardBody>
                                                  </Card>
                                                </Col>
                                              );
                                            })()}

                                            {/* ========== NOTAS ========== */}
                                            {schedule.notes && (
                                              <Col md="12" className="mb-3">
                                                <Card className="shadow-sm border-0">
                                                  <CardBody className="p-3">
                                                    <h6 className="text-uppercase text-xs font-weight-bold text-primary mb-3">
                                                      <i className="ni ni-align-left-2 me-2"></i>
                                                      Notas
                                                    </h6>
                                                    <p className="text-xs mb-0 text-dark" style={{ whiteSpace: 'pre-line' }}>
                                                      {schedule.notes}
                                                    </p>
                                                  </CardBody>
                                                </Card>
                                              </Col>
                                            )}

                                            {/* ========== ACCIONES ========== */}
                                            <Col md="12">
                                              <div className="d-flex gap-2">
                                                {schedule.order_pdf_url && (
                                                  <Button
                                                    color="primary"
                                                    size="sm"
                                                    className="btn-icon shadow-sm"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      viewPDF(schedule.order_pdf_url, `Orden ${schedule.order_number}`);
                                                    }}
                                                  >
                                                    <span className="btn-inner--icon">
                                                      <i className="ni ni-single-copy-04"></i>
                                                    </span>
                                                    <span className="btn-inner--text ms-2">Ver PDF de Orden</span>
                                                  </Button>
                                                )}
                                                <Button
                                                  color="secondary"
                                                  size="sm"
                                                  className="btn-icon shadow-sm me-1"
                                                  title="Descargar info de facturación"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadInfo(schedule);
                                                  }}
                                                >
                                                  <span className="btn-inner--icon">
                                                    <i className="ni ni-cloud-download-95"></i>
                                                  </span>
                                                  <span className="btn-inner--text ms-2">Descargar orden de servicio</span>
                                                </Button>
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  className="btn-icon shadow-sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openUploadModal(schedule);
                                                  }}
                                                >
                                                  <span className="btn-inner--icon">
                                                    <i className="ni ni-cloud-upload-96"></i>
                                                  </span>
                                                  <span className="btn-inner--text ms-2">Subir Factura</span>
                                                </Button>
                                              </div>
                                            </Col>
                                          </Row>
                                        </div>
                                      </Collapse>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>

                  {/* ============================================ */}
                  {/* TAB: FACTURAS EMITIDAS */}
                  {/* ============================================ */}
                  <TabPane tabId="invoiced">
                    <div className="table-responsive p-0">
                      <Table className="align-items-center mb-0" hover>
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Folio / Cliente
                            </th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                              Monto
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Emisión
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Vencimiento
                            </th>
                            <th className="text-secondary opacity-7"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredInvoices.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <i className="ni ni-single-copy-04 text-muted" style={{ fontSize: '3rem' }}></i>
                                <p className="text-xs text-secondary mb-0 mt-3">
                                  No hay facturas emitidas
                                </p>
                                <small className="text-muted">Las facturas emitidas aparecerán aquí</small>
                              </td>
                            </tr>
                          ) : (
                            visibleInvoices.map((invoice) => (
                              <tr key={invoice.id}>
                                {/* Folio / Cliente */}
                                <td>
                                  <div className="d-flex px-2 py-1">
                                    <div className="icon icon-shape icon-sm bg-gradient-warning shadow text-center rounded-circle me-2">
                                      <i className="ni ni-single-copy-04 text-white text-xs"></i>
                                    </div>
                                    <div className="d-flex flex-column justify-content-center">
                                      <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                        Folio {invoice.folio}
                                      </h6>
                                      <p className="text-xs text-secondary mb-0">
                                        {invoice.client_name}
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                {/* Monto */}
                                <td>
                                  <p className="text-sm font-weight-bold mb-0 text-dark">
                                    {formatCurrency(invoice.subtotal)}
                                  </p>
                                  <small className="text-xs text-muted">
                                    + IVA = {formatCurrency(invoice.total)}
                                  </small>
                                </td>

                                {/* Emisión */}
                                <td className="align-middle text-center">
                                  <span className="text-secondary text-xs font-weight-bold">
                                    {parseDate(invoice.issue_date).toLocaleDateString('es-MX', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </td>

                                {/* Vencimiento */}
                                <td className="align-middle text-center">
                                  <span className="text-secondary text-xs font-weight-bold">
                                    {parseDate(invoice.due_date).toLocaleDateString('es-MX', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </td>

                                {/* Acciones */}
                                <td className="align-middle">
                                  <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      className="btn-icon shadow-sm"
                                      onClick={() => viewPDF(invoice.pdf_url, `Factura ${invoice.folio}`)}
                                    >
                                      <span className="btn-inner--icon">
                                        <i className="ni ni-single-copy-04"></i>
                                        Ver factura
                                      </span>
                                    </Button>
                                    {hasPermission('edit_invoices') && (
                                      <Button
                                        color="danger"
                                        outline
                                        size="sm"
                                        className="btn-icon shadow-sm"
                                        onClick={() => openCancelModal(invoice)}
                                      >
                                        <i className="ni ni-fat-remove me-1"></i>
                                        Cancelar
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>

                  {/* ============================================ */}
                  {/* TAB: FACTURAS PAGADAS */}
                  {/* ============================================ */}
                  <TabPane tabId="completed">
                    <div className="table-responsive p-0">
                      <Table className="align-items-center mb-0" hover>
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Folio / Cliente
                            </th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                              Monto
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Fecha de Pago
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Estado
                            </th>
                            <th className="text-secondary opacity-7"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredInvoices.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <i className="ni ni-check-bold text-muted" style={{ fontSize: '3rem' }}></i>
                                <p className="text-xs text-secondary mb-0 mt-3">
                                  No hay facturas pagadas
                                </p>
                                <small className="text-muted">Las facturas pagadas aparecerán aquí</small>
                              </td>
                            </tr>
                          ) : (
                            visibleInvoices.map((invoice) => (
                              <tr key={invoice.id}>
                                {/* Folio / Cliente */}
                                <td>
                                  <div className="d-flex px-2 py-1">
                                    <div className="icon icon-shape icon-sm bg-gradient-success shadow text-center rounded-circle me-2">
                                      <i className="ni ni-check-bold text-white text-xs"></i>
                                    </div>
                                    <div className="d-flex flex-column justify-content-center">
                                      <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                        Folio {invoice.folio}
                                      </h6>
                                      <p className="text-xs text-secondary mb-0">
                                        {invoice.client_name}
                                      </p>
                                    </div>
                                  </div>
                                </td>

                                {/* Monto */}
                                <td>
                                  <p className="text-sm font-weight-bold mb-0 text-dark">
                                    {formatCurrency(invoice.subtotal)}
                                  </p>
                                  <small className="text-xs text-muted">
                                    + IVA = {formatCurrency(invoice.total)}
                                  </small>
                                </td>

                                {/* Fecha de Pago */}
                                <td className="align-middle text-center">
                                  <span className="text-secondary text-xs font-weight-bold">
                                    {invoice.payment_date ? parseDate(invoice.payment_date).toLocaleDateString('es-MX', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    }) : '-'}
                                  </span>
                                </td>

                                {/* Estado */}
                                <td className="align-middle text-center">
                                  <Badge color="" className="badge-sm bg-gradient-success shadow-sm">
                                    <i className="ni ni-check-bold me-1"></i>
                                    PAGADA
                                  </Badge>
                                </td>

                                {/* Acciones */}
                                <td className="align-middle">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="btn-icon shadow-sm"
                                    onClick={() => viewPDF(invoice.pdf_url, `Factura ${invoice.folio}`)}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-single-copy-04"></i>
                                    </span>
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>

                  <TabPane tabId="cancelled">
                    <div className="table-responsive p-0">
                      <Table className="align-items-center mb-0" hover>
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Folio / Cliente
                            </th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                              Monto
                            </th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Cancelada
                            </th>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                              Motivo
                            </th>
                            <th className="text-secondary opacity-7"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredInvoices.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center py-5">
                                <i className="ni ni-fat-remove text-muted" style={{ fontSize: '3rem' }}></i>
                                <p className="text-xs text-secondary mb-0 mt-3">
                                  No hay facturas canceladas
                                </p>
                              </td>
                            </tr>
                          ) : (
                            visibleInvoices.map((invoice) => (
                              <tr key={invoice.id}>
                                <td>
                                  <div className="d-flex px-2 py-1">
                                    <div className="icon icon-shape icon-sm bg-gradient-danger shadow text-center rounded-circle me-2">
                                      <i className="ni ni-fat-remove text-white text-xs"></i>
                                    </div>
                                    <div className="d-flex flex-column justify-content-center">
                                      <h6 className="mb-0 text-sm font-weight-bold text-dark">
                                        Folio {invoice.folio}
                                      </h6>
                                      <p className="text-xs text-secondary mb-0">
                                        {invoice.client_name}
                                      </p>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <p className="text-sm font-weight-bold mb-0 text-dark">
                                    {formatCurrency(invoice.subtotal)}
                                  </p>
                                  <small className="text-xs text-muted">
                                    + IVA = {formatCurrency(invoice.total)}
                                  </small>
                                </td>
                                <td className="align-middle text-center">
                                  <span className="text-secondary text-xs font-weight-bold">
                                    {invoice.cancelled_at
                                      ? parseDate(invoice.cancelled_at).toLocaleDateString('es-MX', {
                                          day: '2-digit',
                                          month: 'short',
                                          year: 'numeric'
                                        })
                                      : '-'}
                                  </span>
                                </td>
                                <td className="align-middle">
                                  <p className="text-xs text-dark mb-0" style={{ maxWidth: '320px', whiteSpace: 'normal' }}>
                                    {invoice.cancellation_reason || 'Sin motivo registrado'}
                                  </p>
                                  {invoice.cancelled_by_name && (
                                    <small className="text-muted">Por {invoice.cancelled_by_name}</small>
                                  )}
                                </td>
                                <td className="align-middle">
                                  <div className="d-flex gap-2 flex-wrap">
                                    {invoice.pdf_url && (
                                      <Button
                                        color="secondary"
                                        outline
                                        size="sm"
                                        onClick={() => viewPDF(invoice.pdf_url, `Factura cancelada ${invoice.folio}`)}
                                      >
                                        Ver factura
                                      </Button>
                                    )}
                                    {hasPermission('delete_invoices') && (
                                      <Button
                                        color="danger"
                                        outline
                                        size="sm"
                                        onClick={() => openDeleteModal(invoice)}
                                      >
                                        <i className="ni ni-fat-remove me-1"></i>
                                        Eliminar
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </TabPane>
                </TabContent>
                {activeTab !== 'pending' && filteredInvoices.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center px-3 pt-3">
                    <small className="text-muted">
                      Mostrando {(currentPage - 1) * rowsPerPage + 1} - {Math.min(currentPage * rowsPerPage, filteredInvoices.length)} de {filteredInvoices.length}
                    </small>
                    <div className="d-flex gap-2">
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      >
                        Anterior
                      </Button>
                      <span className="text-xs text-muted align-self-center">
                        Pagina {currentPage} de {totalInvoicePages}
                      </span>
                      <Button
                        color="secondary"
                        size="sm"
                        outline
                        disabled={currentPage >= totalInvoicePages}
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalInvoicePages))}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ============================================ */}
      {/* MODALES */}
      {/* ============================================ */}
      <Modal isOpen={cancelModalOpen} toggle={() => closeCancelModal()} centered>
        <ModalHeader
          toggle={() => closeCancelModal()}
          className="bg-gradient-danger text-white"
        >
          Cancelar factura {invoiceToCancel?.folio}
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-warning text-sm">
            La factura se conservará en el historial como cancelada y su programación
            regresará a <strong>Pendientes de Facturar</strong> para subir la factura corregida.
          </div>
          <Label for="invoice-cancellation-reason" className="form-control-label">
            Motivo de cancelación
          </Label>
          <Input
            id="invoice-cancellation-reason"
            type="textarea"
            rows="4"
            maxLength="1000"
            value={cancellationReason}
            invalid={Boolean(cancelError)}
            placeholder="Ej. RFC incorrecto, monto equivocado o datos fiscales por corregir"
            onChange={(e) => {
              setCancellationReason(e.target.value);
              if (cancelError) setCancelError('');
            }}
            disabled={cancellingInvoice}
          />
          {cancelError && (
            <div className="text-danger text-xs mt-2">{cancelError}</div>
          )}
          <small className="text-muted d-block mt-2">
            La factura original y su PDF no se eliminarán.
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => closeCancelModal()}
            disabled={cancellingInvoice}
          >
            Volver
          </Button>
          <Button
            color="danger"
            onClick={handleCancelInvoice}
            disabled={cancellingInvoice || !cancellationReason.trim()}
          >
            {cancellingInvoice ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Cancelando...
              </>
            ) : (
              'Cancelar y refacturar'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModalOpen} toggle={() => closeDeleteModal()} centered>
        <ModalHeader
          toggle={() => closeDeleteModal()}
          className="bg-gradient-danger text-white"
        >
          Eliminar factura {invoiceToDelete?.folio}
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-danger text-sm">
            Esta acción es <strong>permanente</strong>: se borrará el registro y su PDF
            del historial, y no se puede deshacer. Úsala solo para limpiar una factura
            cancelada por error (p. ej. folio fiscal duplicado o datos incorrectos) y
            así poder volver a registrarla correctamente.
          </div>
          {invoiceToDelete?.cancellation_reason && (
            <p className="text-xs text-secondary mb-0">
              Motivo de cancelación registrado: <strong>{invoiceToDelete.cancellation_reason}</strong>
            </p>
          )}
          {deleteError && (
            <div className="text-danger text-xs mt-2">{deleteError}</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => closeDeleteModal()}
            disabled={deletingInvoice}
          >
            Volver
          </Button>
          <Button
            color="danger"
            onClick={handleDeleteInvoice}
            disabled={deletingInvoice}
          >
            {deletingInvoice ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Eliminando...
              </>
            ) : (
              'Eliminar permanentemente'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <InvoiceUploadModal
        isOpen={uploadModalOpen}
        toggle={() => setUploadModalOpen(false)}
        scheduleData={selectedSchedule}
        onConfirm={handleUploadConfirm}
      />

      {pdfViewerOpen && (
        <PDFViewerModal
          isOpen={pdfViewerOpen}
          toggle={() => setPdfViewerOpen(false)}
          pdfUrl={pdfUrl}
          title={pdfTitle}
        />
      )}
    </>
  );
};

export default InvoiceInbox;
